import Constants from '../Constants';
import {HttpClient} from "./HttpClient";

type MetricName = 'RENDER_INIT' | 'RENDER_SUCCESS' | 'RENDER_ERROR' | 'TOKEN_RECEIVED';
type MetricType = 'Counter' | 'Timer';

interface Metric {
    name: MetricName;
    type: MetricType;
    elapsedMillis: number;
}

interface Metrics {
    componentName: string;
    siteDomain?: string;
    metrics: Metric[];
}

interface Logs {
    componentName: string;
    siteDomain?: string;
    logs: Log[];
}

type LogLevel = 'INFO' | 'WARNING' | 'ERROR';

interface Log {
    level: LogLevel;
    content: string;
    customerId?: number;
    orderId?: number;
}

interface MetricsConfig {
    initTimestamp: number;
    componentName: string;
    siteDomain: string;
    customerId?: number;
    orderId?: number;
}

const SET_INTERVAL_TIMER: number = 5000;

export default class MetricsService extends HttpClient {
    initTimestamp: number;
    componentName: string;
    metricsBuffer: Metric[] = [];
    logsBuffer: Log[] = [];
    siteDomain: string;
    orderId: number | undefined;
    customerId: number | undefined;

    constructor(config: MetricsConfig) {
        super()
        this.initTimestamp = config.initTimestamp;
        this.componentName = config.componentName;
        this.siteDomain = config.siteDomain;
        this.orderId = config.orderId;
        this.customerId = config.customerId;
        setInterval(() => {
            if (this.metricsBuffer.length) {
                this.pushMetric();
            }
            if (this.logsBuffer.length) {
                this.pushLogs();
            }
        }, SET_INTERVAL_TIMER);
    }

    async pushLogToApi() {
        const logs: Logs = {
            siteDomain: this.siteDomain,
            componentName: this.componentName,
            logs: this.logsBuffer,
        };

        return super.post(Constants.API.URI.LOGS, logs);
    }

    async pushLogs() {
        return new Promise<boolean>((resolve, reject) => {
            this.pushLogToApi()
                .then((r) => {
                    this.logsBuffer = [];
                    resolve(r.status === 200);
                })
                .catch((e) => reject(e));
        });
    }

    setLogsBuffer(errorContent: string) {
        const log: Log = {
            level: 'ERROR',
            content: errorContent,
            orderId: this.orderId,
            customerId: this.customerId,
        };
        this.logsBuffer.push(log);
        if (this.logsBuffer.length >= 100) {
            this.pushLogToApi();
        }
    }

    async pushMetricToApi() {
        const metrics: Metrics = {
            componentName: this.componentName,
            siteDomain: this.siteDomain,
            metrics: this.metricsBuffer,
        };
        return super.post(Constants.API.URI.METRICS, metrics);
    }

    async pushMetric() {
        return new Promise<boolean>((resolve, reject) => {
            this.pushMetricToApi()
                .then((r) => {
                    this.metricsBuffer = [];
                    resolve(r.status === 200);
                })
                .catch((e) => reject(e));
        });
    }

    private getElapsedMillis(initTimestamp: number): number {
        return Date.now() - initTimestamp;
    }

    private setMetricsBuffer(name: MetricName, type: MetricType) {
        const metric: Metric = {
            name: name,
            type: type,
            elapsedMillis: this.getElapsedMillis(this.initTimestamp),
        };
        this.metricsBuffer.push(metric);
        if (this.metricsBuffer.length >= 100) {
            this.pushMetric();
        }
    }

    firstEventToken(): MetricsService {
        this.setMetricsBuffer('TOKEN_RECEIVED', 'Timer');
        return this;
    }

    renderInit(): MetricsService {
        this.setMetricsBuffer('RENDER_INIT', 'Counter');
        return this;
    }

    renderSuccess(): MetricsService {
        this.setMetricsBuffer('RENDER_SUCCESS', 'Timer');
        return this;
    }

    renderError(): MetricsService {
        this.setMetricsBuffer('RENDER_ERROR', 'Counter');
        return this;
    }
}

